// R17 Wave 0 extraction — pure refactor, no behavior change
import React, { useState } from 'react';
import { Printer, ChevronDown, ChevronUp } from 'lucide-react';
import type { UserReportUser } from '../../types/report';
import UserReportManualChecksSection from './UserReportManualChecksSection';

export interface UserReportUserCardProps {
  user: UserReportUser;
  expanded: boolean;
  onToggle: () => void;
  onPrint: () => void;
  isPrinting: boolean;
  formatCurrency: (amount: number) => string;
}

/**
 * Per-user card body for the User Report page. Previously lived as an inline
 * `UserCard` component inside UserReport.tsx; promoted to its own file in
 * R17 Wave 0 so subsequent Task A (reconciliation columns / new stat tiles)
 * doesn't push the parent past the 400-LOC cap.
 */
const UserReportUserCard: React.FC<UserReportUserCardProps> = ({
  user,
  expanded,
  onToggle,
  onPrint,
  isPrinting,
  formatCurrency,
}) => {
  // R11 #1: inner expansion is now keyed by client_id (was account_id).
  // Each client row aggregates all its sub-accounts; per-row account_name
  // surfaces the sub-account detail inside the expanded table.
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());

  const toggleClientExpansion = (clientId: number) => {
    setExpandedClients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* User Header */}
      <div className="px-6 py-4 flex items-center justify-between bg-gray-50">
        <button
          onClick={onToggle}
          className="flex-1 flex items-center text-left hover:bg-gray-100 transition-colors -m-4 p-4 rounded-lg"
        >
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 flex items-center">
              {user.user_name}
              {expanded ? (
                <ChevronUp className="h-5 w-5 ml-2 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 ml-2 text-gray-400" />
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{user.user_email}</p>
            {/* R17 Task A: replace the single "User Commission" tile with
                the four reconciliation splits (Trade Gross / Check Gross /
                Trade User Comm / Check User Comm) plus the combined Grand
                Total (existing total_commission). Blue = trade side,
                emerald = check side — matches the summary tile colour
                language for cross-surface consistency. Falls back to the
                combined total_* fields when the backend hasn't yet been
                upgraded to R17 (backward compat with cached payloads). */}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <div className="bg-blue-50 border border-blue-100 rounded px-2 py-1.5">
                <p className="text-gray-600">Trade Gross</p>
                <p className="font-semibold text-blue-700">
                  {formatCurrency(user.trade_gross_commission ?? user.total_gross_commission ?? 0)}
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded px-2 py-1.5">
                <p className="text-gray-600">Check Gross</p>
                <p className="font-semibold text-emerald-700">
                  {formatCurrency(user.check_gross_commission ?? 0)}
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded px-2 py-1.5">
                <p className="text-gray-600">Trade User Comm</p>
                <p className="font-semibold text-blue-700">
                  {formatCurrency(user.trade_user_commission ?? user.total_commission ?? 0)}
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded px-2 py-1.5">
                <p className="text-gray-600">Check User Comm</p>
                <p className="font-semibold text-emerald-700">
                  {formatCurrency(user.check_user_commission ?? 0)}
                </p>
              </div>
              <div className="bg-green-100 border border-green-200 rounded px-2 py-1.5">
                <p className="text-gray-600">Grand Total</p>
                <p className="font-semibold text-green-800">{formatCurrency(user.total_commission)}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center space-x-6 text-xs">
              <div>
                <span className="text-gray-500">Trade Transactions: </span>
                <span className="font-medium text-gray-900">
                  {user.trade_transaction_count ?? user.transaction_count}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Checks: </span>
                <span className="font-medium text-gray-900">
                  {user.check_count ?? user.manual_checks?.length ?? 0}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Clients: </span>
                <span className="font-medium text-gray-900">{user.clients.length}</span>
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={onPrint}
          disabled={isPrinting}
          className="ml-4 flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          <Printer className="h-4 w-4 mr-2" />
          {isPrinting ? 'Printing...' : 'Print PDF'}
        </button>
      </div>

      {/* Clients */}
      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <div className="space-y-3">
            {user.clients.map((client) => {
              // R13 Task H: in multi-day summary mode the backend returns
              // transactions: []. Suppress the expand affordance entirely so
              // the row reads as a pure summary card — clicking it would
              // open an empty drilldown, which is just confusing.
              const hasTransactionDetail = (client.transactions?.length ?? 0) > 0;
              const isClientExpanded = hasTransactionDetail && expandedClients.has(client.client_id);
              const clientGross = client.gross_commission ??
                client.transactions.reduce((s, t) => s + (t.gross_commission || 0), 0);
              return (
                <div key={client.client_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => hasTransactionDetail && toggleClientExpansion(client.client_id)}
                    disabled={!hasTransactionDetail}
                    className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                      hasTransactionDetail ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                    }`}
                  >
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-gray-900">
                        {client.client_name}
                      </p>
                      <div className="mt-1 flex gap-4 text-xs">
                        <span className="text-gray-600">
                          {client.transaction_count} transactions
                        </span>
                        <span className="text-blue-700 font-medium">
                          Gross: {formatCurrency(clientGross)}
                        </span>
                        <span className="text-green-700 font-medium">
                          User: {formatCurrency(client.commission_amount)}
                        </span>
                      </div>
                    </div>
                    {hasTransactionDetail && (
                      <div className="ml-4">
                        {isClientExpanded ? (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    )}
                  </button>

                  {isClientExpanded && (
                    <div className="border-t border-gray-200 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Bought</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Sold</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Security</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Price</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Account</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Rate</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Gross Comm.</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Comm. %</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">User Comm.</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {client.transactions.map((transaction) => (
                            <tr key={transaction.id} className="hover:bg-gray-50">
                              {/* R11 #2: backend already returns a clean 'Y-m-d' string —
                                  no client-side formatDate needed. */}
                              <td className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap">{transaction.date ?? ''}</td>
                              <td className="px-3 py-2 text-xs text-right text-gray-900">{transaction.bought > 0 ? transaction.bought.toLocaleString() : ''}</td>
                              <td className="px-3 py-2 text-xs text-right text-gray-900">{transaction.sold > 0 ? transaction.sold.toLocaleString() : ''}</td>
                              <td className="px-3 py-2 text-xs text-gray-900">{transaction.security}</td>
                              <td className="px-3 py-2 text-xs text-right text-gray-900">{formatCurrency(transaction.price)}</td>
                              <td className="px-3 py-2 text-xs text-gray-900">{transaction.account_name ?? ''}</td>
                              <td className="px-3 py-2 text-xs text-right text-gray-600">{(transaction.rate ?? 0).toFixed(4)}</td>
                              <td className="px-3 py-2 text-xs text-right font-medium text-blue-700">{formatCurrency(transaction.gross_commission || 0)}</td>
                              <td className="px-3 py-2 text-xs text-right text-gray-700">{(transaction.percentage ?? 0).toFixed(2)}%</td>
                              <td className="px-3 py-2 text-xs text-right font-medium text-green-700">{formatCurrency(transaction.salesman_commission ?? transaction.user_commission ?? 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* R17 Task B will render the per-user Manual Checks section here.
              Wave 0 scaffold returns null so behavior matches master. */}
          <UserReportManualChecksSection user={user} />
        </div>
      )}
    </div>
  );
};

export default UserReportUserCard;
